import { useCallback, useEffect, useState } from "react"
import type { CombinedError } from "urql"
import { useQuery } from "urql"

import { GetProductAislesPreferenceWarehouseShelvingListDocument } from "@/graphql/codegen/graphql"
import type { Query, WarehouseShelving, WarehouseShelvingQueryListArgs } from "@/graphql/codegen/graphql"

interface UseGetProductAislesPreferenceWarehouseShelvingsReturn {
  warehouseShelvings: WarehouseShelving[]
  isFetching: boolean
  error?: CombinedError
  reset: () => void
}

const LIMIT = 1000

export const useGetProductAislesPreferenceWarehouseShelvings = (
  warehouseAreaId?: string
): UseGetProductAislesPreferenceWarehouseShelvingsReturn => {
  const [warehouseShelvings, setWarehouseShelvings] = useState<WarehouseShelving[]>([])
  const [offset, setOffset] = useState(0)
  const [hasNextPage, setHasNextPage] = useState(true)

  const [{ data, fetching, error }] = useQuery<Pick<Query, "warehouseShelving">, WarehouseShelvingQueryListArgs>({
    query: GetProductAislesPreferenceWarehouseShelvingListDocument,
    variables: { input: { filter: { warehouseAreaId }, limit: LIMIT, offset } },
    pause: !warehouseAreaId,
  })

  useEffect(() => {
    if (data?.warehouseShelving.list.__typename === "WarehouseShelvingListSuccess") {
      const paginatedWarehouseShelvings = data.warehouseShelving.list.warehouseShelvings
      setWarehouseShelvings((prev: WarehouseShelving[]) =>
        offset === 0 ? paginatedWarehouseShelvings : [...prev, ...paginatedWarehouseShelvings]
      )

      if (paginatedWarehouseShelvings.length < LIMIT) {
        setHasNextPage(false)
      }

      loadMore()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const loadMore = () => {
    if (hasNextPage) {
      setOffset((prevOffset) => prevOffset + LIMIT)
    }
  }

  const reset = useCallback(() => {
    setOffset(0)
    setWarehouseShelvings([])
    setHasNextPage(true)
  }, [setOffset, setWarehouseShelvings, setHasNextPage])

  return {
    warehouseShelvings,
    isFetching: fetching,
    error,
    reset,
  }
}
